import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import qrOrderService from '../services/qrOrderService';

export default function useQROrders({ autoRefresh = true } = {}) {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const prevCountRef = useRef(0);
  const intervalRef = useRef(null);

  const fetchPending = useCallback(async () => {
    try {
      const data = await qrOrderService.getPending();
      const orders = data || [];
      // Notify on new orders
      if (orders.length > prevCountRef.current && prevCountRef.current > 0) {
        const newCount = orders.length - prevCountRef.current;
        toast(`${newCount} new QR order${newCount > 1 ? 's' : ''} received!`, {
          icon: '\uD83D\uDD14',
          duration: 5000,
        });
        // Play notification sound
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczHjqIxN3LcjwjOYHA2MxtOCQ6gsHZzm45IzmBwdjNbjglOYHB2c1uOCU5');
          audio.volume = 0.3;
          audio.play().catch(() => {});
        } catch {}
      }
      prevCountRef.current = orders.length;
      setPendingOrders(orders);
    } catch (err) {
      console.error('Failed to fetch pending orders:', err);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const data = await qrOrderService.getAll(statusFilter || undefined, dateFilter || undefined);
      setAllOrders(data || []);
    } catch (err) {
      console.error('Failed to fetch all orders:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter]);

  // Poll pending orders — only when autoRefresh is enabled
  useEffect(() => {
    fetchPending();
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchPending, 5000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchPending, autoRefresh]);

  // Fetch all orders when filter changes
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const approveOrder = async (id) => {
    try {
      const result = await qrOrderService.approve(id);
      toast.success('Order approved - sent to kitchen');
      setPendingOrders((prev) => prev.filter((o) => o.id !== id));
      await fetchAll();
      return result;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to approve order');
      throw err;
    }
  };

  const rejectOrder = async (id, reason) => {
    try {
      const result = await qrOrderService.reject(id, reason);
      toast.success('Order rejected');
      setPendingOrders((prev) => prev.filter((o) => o.id !== id));
      await fetchAll();
      return result;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to reject order');
      throw err;
    }
  };

  return {
    pendingOrders,
    allOrders,
    loading,
    statusFilter,
    setStatusFilter,
    dateFilter,
    setDateFilter,
    approveOrder,
    rejectOrder,
    refreshPending: fetchPending,
    refreshAll: fetchAll,
  };
}
